/**
 * WhatsApp Business Calling in the browser.
 *
 * The agent's browser owns the RTCPeerConnection; the API only relays SDP to and
 * from Meta. Meta does not accept trickle ICE on this API, so both offers and
 * answers are sent once ICE gathering has settled.
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../api/client.js'
import { showBrowserNotification } from '../utils/browserNotifications.js'
import { playWhatsAppCallSound, stopWhatsAppCallSound } from '../utils/whatsappNotificationSound.js'

export type WhatsAppCallState =
  | 'idle'
  /** Inbound call ringing on our side, waiting for the agent to answer. */
  | 'incoming'
  /** Setting up mic + SDP locally; nothing has left the browser yet. */
  | 'dialing'
  /** Placed with Meta — the customer's handset is ringing. */
  | 'ringing'
  /** They answered; media is still negotiating. */
  | 'connecting'
  | 'connected'
  | 'ended'
  | 'failed'

export interface ActiveWhatsAppCall {
  call_id: string
  chat_id: string | null
  wa_id: string
  direction: 'inbound' | 'outbound'
  status: string
  sdp_offer: string | null
  sdp_answer: string | null
  started_at: string
  profile_name: string | null
  display_name: string | null
}

/** Server-side statuses that mean the call is over, whoever ended it. */
const TERMINAL_STATUSES = new Set(['completed', 'failed', 'rejected'])

const ICE_SERVERS: RTCIceServer[] = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
]

/** Meta expects one complete SDP, so wait for gathering rather than trickling. */
async function waitForIceGathering(pc: RTCPeerConnection, timeoutMs = 4000): Promise<void> {
  if (pc.iceGatheringState === 'complete') return
  await new Promise<void>((resolve) => {
    const finish = (): void => {
      clearTimeout(timer)
      pc.removeEventListener('icegatheringstatechange', onChange)
      resolve()
    }
    const onChange = (): void => {
      if (pc.iceGatheringState === 'complete') finish()
    }
    // Gathering can stall behind a restrictive NAT; ship what we have rather
    // than leaving the agent staring at a call that never dials.
    const timer = setTimeout(finish, timeoutMs)
    pc.addEventListener('icegatheringstatechange', onChange)
  })
}

export function useWhatsAppCall() {
  const [state, setState] = useState<WhatsAppCallState>('idle')
  const [activeCall, setActiveCall] = useState<ActiveWhatsAppCall | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [muted, setMuted] = useState(false)
  const [durationSeconds, setDurationSeconds] = useState(0)

  const pcRef = useRef<RTCPeerConnection | null>(null)
  const localStreamRef = useRef<MediaStream | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  /** Wall-clock moment the call was answered, so the timer starts from zero. */
  const connectedAtRef = useRef<number | null>(null)

  // Audio refs for ringtone and ringback
  const ringtoneCtxRef = useRef<AudioContext | null>(null)
  const ringtoneTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const ringtoneNodesRef = useRef<{ o1: OscillatorNode; o2: OscillatorNode; g: GainNode } | null>(null)
  const ringbackCtxRef = useRef<AudioContext | null>(null)
  const ringbackTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const ringbackNodesRef = useRef<{ o1: OscillatorNode; o2: OscillatorNode; g: GainNode } | null>(null)

  // Start ringtone for incoming calls (US standard: 440+480Hz, 2s on / 4s off)
  const startRingtone = useCallback(() => {
    try {
      if (ringtoneTimerRef.current) return // already ringing
      const ctx = new AudioContext()
      ringtoneCtxRef.current = ctx

      const ringBurst = (): void => {
        const o1 = ctx.createOscillator()
        const o2 = ctx.createOscillator()
        const g = ctx.createGain()
        o1.type = 'sine'
        o1.frequency.value = 440
        o2.type = 'sine'
        o2.frequency.value = 480

        g.gain.setValueAtTime(0, ctx.currentTime)
        g.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.04)
        g.gain.setValueAtTime(0.08, ctx.currentTime + 1.96)
        g.gain.linearRampToValueAtTime(0, ctx.currentTime + 2.0)

        o1.connect(g)
        o2.connect(g)
        g.connect(ctx.destination)
        o1.start(ctx.currentTime)
        o2.start(ctx.currentTime)
        o1.stop(ctx.currentTime + 2.0)
        o2.stop(ctx.currentTime + 2.0)
        ringtoneNodesRef.current = { o1, o2, g }
      }

      ringBurst()
      ringtoneTimerRef.current = setInterval(ringBurst, 6000) // 2s on + 4s off
    } catch {
      // ignore audio errors
    }
  }, [])

  const stopRingtone = useCallback(() => {
    try {
      if (ringtoneTimerRef.current) {
        clearInterval(ringtoneTimerRef.current)
        ringtoneTimerRef.current = null
      }
      if (ringtoneNodesRef.current) {
        const { o1, o2, g } = ringtoneNodesRef.current
        const ctx = ringtoneCtxRef.current
        if (ctx) {
          g.gain.setValueAtTime(g.gain.value, ctx.currentTime)
          g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05)
          o1.stop(ctx.currentTime + 0.05)
          o2.stop(ctx.currentTime + 0.05)
        }
        ringtoneNodesRef.current = null
      }
      if (ringtoneCtxRef.current) {
        ringtoneCtxRef.current.close()
        ringtoneCtxRef.current = null
      }
    } catch {
      // ignore
    }
  }, [])

  // Start ringback tone for outgoing calls (lower volume, same US cadence)
  const startRingback = useCallback(() => {
    try {
      if (ringbackTimerRef.current) return
      const ctx = new AudioContext()
      ringbackCtxRef.current = ctx

      const ringBurst = (): void => {
        const o1 = ctx.createOscillator()
        const o2 = ctx.createOscillator()
        const g = ctx.createGain()
        o1.type = 'sine'
        o1.frequency.value = 440
        o2.type = 'sine'
        o2.frequency.value = 480

        g.gain.setValueAtTime(0, ctx.currentTime)
        g.gain.linearRampToValueAtTime(0.04, ctx.currentTime + 0.04)
        g.gain.setValueAtTime(0.04, ctx.currentTime + 1.96)
        g.gain.linearRampToValueAtTime(0, ctx.currentTime + 2.0)

        o1.connect(g)
        o2.connect(g)
        g.connect(ctx.destination)
        o1.start(ctx.currentTime)
        o2.start(ctx.currentTime)
        o1.stop(ctx.currentTime + 2.0)
        o2.stop(ctx.currentTime + 2.0)
        ringbackNodesRef.current = { o1, o2, g }
      }

      ringBurst()
      ringbackTimerRef.current = setInterval(ringBurst, 6000)
    } catch {
      // ignore
    }
  }, [])

  const stopRingback = useCallback(() => {
    try {
      if (ringbackTimerRef.current) {
        clearInterval(ringbackTimerRef.current)
        ringbackTimerRef.current = null
      }
      if (ringbackNodesRef.current) {
        const { o1, o2, g } = ringbackNodesRef.current
        const ctx = ringbackCtxRef.current
        if (ctx) {
          g.gain.setValueAtTime(g.gain.value, ctx.currentTime)
          g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05)
          o1.stop(ctx.currentTime + 0.05)
          o2.stop(ctx.currentTime + 0.05)
        }
        ringbackNodesRef.current = null
      }
      if (ringbackCtxRef.current) {
        ringbackCtxRef.current.close()
        ringbackCtxRef.current = null
      }
    } catch {
      // ignore
    }
  }, [])

  const teardown = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    pcRef.current?.close()
    pcRef.current = null
    localStreamRef.current?.getTracks().forEach((t) => t.stop())
    localStreamRef.current = null
    if (audioRef.current) {
      audioRef.current.srcObject = null
    }
    connectedAtRef.current = null
    setMuted(false)
    // Stop any playing tones
    stopRingtone()
    stopRingback()
  }, [stopRingtone, stopRingback])

  /** Single exit path: stop media, drop the call, hide the overlay. */
  const endLocally = useCallback(
    (reason: string | null) => {
      teardown()
      setActiveCall(null)
      setError(reason)
      setState('idle')
    },
    [teardown],
  )

  useEffect(() => {
    // One hidden element carries remote audio for every call this hook handles.
    const el = document.createElement('audio')
    el.autoplay = true
    el.style.display = 'none'
    document.body.appendChild(el)
    audioRef.current = el
    return () => {
      teardown()
      el.remove()
      audioRef.current = null
    }
  }, [teardown])

  // Talk time only — starts when the call is genuinely answered.
  useEffect(() => {
    if (state !== 'connected') {
      setDurationSeconds(0)
      return
    }
    const startedAt = connectedAtRef.current ?? Date.now()
    const timer = setInterval(() => setDurationSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000)
    return () => clearInterval(timer)
  }, [state])

  // Play ringtone for incoming calls, ringback for outgoing
  useEffect(() => {
    if (state === 'incoming') {
      startRingtone()
    } else if (state === 'dialing' || state === 'ringing') {
      startRingback()
    } else {
      stopRingtone()
      stopRingback()
      // Also stop the WhatsApp call sound from useIncomingWhatsAppCalls
      stopWhatsAppCallSound()
    }
    return () => {
      stopRingtone()
      stopRingback()
      stopWhatsAppCallSound()
    }
  }, [state, startRingtone, stopRingtone, startRingback, stopRingback])

  const buildPeerConnection = useCallback(async (): Promise<RTCPeerConnection> => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    localStreamRef.current = stream
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })
    stream.getTracks().forEach((track) => pc.addTrack(track, stream))
    pc.ontrack = (event) => {
      const [remote] = event.streams
      if (remote && audioRef.current) audioRef.current.srcObject = remote
    }
    pc.onconnectionstatechange = () => {
      // Deliberately does NOT promote to 'connected'. Meta's media server
      // completes the WebRTC handshake as soon as the call is placed, well
      // before the customer picks up — treating that as "answered" started the
      // talk-time clock while their phone was still ringing. Only the server's
      // own call status (set from Meta's `connect` webhook) means answered.
      if (pc.connectionState === 'failed') {
        setError('Media connection failed')
        setState('failed')
      }
    }
    pcRef.current = pc
    return pc
  }, [])

  /**
   * One poller for the whole life of a call.
   *
   * It picks up the SDP answer Meta delivers by webhook rather than inline, and
   * — just as importantly — notices when the far end hangs up. Without the
   * latter the overlay stayed on screen forever after the customer ended the
   * call, because nothing else ever told the browser.
   */
  const startCallPolling = useCallback(
    (callId: string, opts: { expectAnswer: boolean }) => {
      if (pollRef.current) clearInterval(pollRef.current)
      let attempts = 0
      pollRef.current = setInterval(() => {
        attempts += 1
        // ~60s of ringing with no answer is a dead call.
        if (opts.expectAnswer && attempts > 40 && connectedAtRef.current === null) {
          endLocally('No answer')
          return
        }
        void api
          .get<{ call: ActiveWhatsAppCall }>(`/agent/whatsapp-calls/${callId}`)
          .then(async (res) => {
            const call = res.call
            if (!call) return
            if (TERMINAL_STATUSES.has(call.status)) {
              endLocally(call.status === 'rejected' ? 'Call declined' : null)
              return
            }
            const pc = pcRef.current
            if (call.sdp_answer && pc && pc.signalingState === 'have-local-offer') {
              await pc.setRemoteDescription({ type: 'answer', sdp: call.sdp_answer })
              setState('connecting')
            }
            // Meta only reports `connected` once the customer has accepted.
            if (call.status === 'connected') {
              connectedAtRef.current ??= Date.now()
              setState('connected')
            }
          })
          .catch(() => {
            // transient — the next tick retries
          })
      }, 1500)
    },
    [endLocally],
  )

  const placeCall = useCallback(
    async (target: { chatId?: string; phone?: string; label?: string }) => {
      setError(null)
      setState('dialing')
      // Show the overlay before touching the mic or the network: getUserMedia,
      // ICE gathering and the API round-trip together take seconds, and until
      // now the agent got no feedback at all in that window.
      setActiveCall({
        call_id: '',
        chat_id: target.chatId ?? null,
        wa_id: target.phone ?? '',
        direction: 'outbound',
        status: 'dialing',
        sdp_offer: null,
        sdp_answer: null,
        started_at: new Date().toISOString(),
        profile_name: target.label ?? null,
        display_name: target.label ?? null,
      })
      try {
        const pc = await buildPeerConnection()
        const offer = await pc.createOffer({ offerToReceiveAudio: true })
        await pc.setLocalDescription(offer)
        await waitForIceGathering(pc)

        const res = await api.post<{ callId: string; chatId: string; waId: string }>(
          '/agent/whatsapp-calls',
          {
            ...(target.chatId ? { chatId: target.chatId } : {}),
            ...(target.phone ? { phone: target.phone } : {}),
            sdp: pc.localDescription?.sdp ?? '',
          },
        )

        setActiveCall((prev) => ({
          ...(prev ?? ({} as ActiveWhatsAppCall)),
          call_id: res.callId,
          chat_id: res.chatId,
          wa_id: res.waId,
          direction: 'outbound',
          status: 'ringing',
          sdp_offer: pc.localDescription?.sdp ?? null,
          sdp_answer: null,
          started_at: new Date().toISOString(),
          profile_name: target.label ?? null,
          display_name: target.label ?? null,
        }))
        setState('ringing')
        startCallPolling(res.callId, { expectAnswer: true })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not start the call')
        setState('failed')
        teardown()
      }
    },
    [buildPeerConnection, startCallPolling, teardown],
  )

  const acceptCall = useCallback(
    async (call: ActiveWhatsAppCall) => {
      if (!call.sdp_offer) {
        setError('Call offer not available yet')
        return
      }
      setError(null)
      setState('connecting')
      try {
        const pc = await buildPeerConnection()
        await pc.setRemoteDescription({ type: 'offer', sdp: call.sdp_offer })
        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)
        await waitForIceGathering(pc)

        await api.post(`/agent/whatsapp-calls/${call.call_id}/accept`, {
          sdp: pc.localDescription?.sdp ?? '',
        })
        connectedAtRef.current = Date.now()
        setState('connected')
        setActiveCall({ ...call, status: 'connected' })
        // Keep watching: the customer may hang up before we do.
        startCallPolling(call.call_id, { expectAnswer: false })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not answer the call')
        setState('failed')
        teardown()
      }
    },
    [buildPeerConnection, startCallPolling, teardown],
  )

  const rejectCall = useCallback(
    async (callId: string) => {
      try {
        await api.post(`/agent/whatsapp-calls/${callId}/reject`)
      } catch {
        // the call is going away regardless
      }
      endLocally(null)
    },
    [endLocally],
  )

  const hangUp = useCallback(async () => {
    const callId = activeCall?.call_id
    // Tear the local side down first so the overlay closes immediately; the
    // API call is best-effort cleanup on Meta's side.
    endLocally(null)
    if (callId) {
      try {
        await api.post(`/agent/whatsapp-calls/${callId}/terminate`)
      } catch {
        // already torn down on Meta's side
      }
    }
  }, [activeCall, endLocally])

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current
    if (!stream) return
    const next = !muted
    stream.getAudioTracks().forEach((t) => {
      t.enabled = !next
    })
    setMuted(next)
  }, [muted])

  /** Surfaces an inbound ringing call so the UI can pop the answer prompt. */
  const setIncoming = useCallback((call: ActiveWhatsAppCall) => {
    setActiveCall(call)
    setState('incoming')
  }, [])

  return {
    state,
    activeCall,
    error,
    muted,
    durationSeconds,
    placeCall,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
    setIncoming,
    isBusy: state !== 'idle' && state !== 'ended',
  }
}

/**
 * Watches for inbound calls ringing on this tenant's number.
 * Paused while a call is in progress so an answered call isn't re-announced.
 */
export function useIncomingWhatsAppCalls(
  enabled: boolean,
  onIncoming: (call: ActiveWhatsAppCall) => void,
): void {
  const seenRef = useRef<Set<string>>(new Set())
  const handlerRef = useRef(onIncoming)
  handlerRef.current = onIncoming

  useEffect(() => {
    if (!enabled) return
    const tick = async (): Promise<void> => {
      try {
        const res = await api.get<{ calls: ActiveWhatsAppCall[] }>('/agent/whatsapp-calls/active')
        for (const call of res.calls ?? []) {
          if (call.direction !== 'inbound' || call.status !== 'ringing') continue
          if (seenRef.current.has(call.call_id)) continue
          seenRef.current.add(call.call_id)
          // Play WhatsApp call ringtone and show browser notification
          playWhatsAppCallSound()
          const callerName = call.display_name || call.profile_name || `+${call.wa_id}`
          showBrowserNotification('Incoming WhatsApp Call', `${callerName} is calling...`)
          handlerRef.current(call)
        }
      } catch {
        // ignore — next tick retries
      }
    }
    void tick()
    const timer = setInterval(() => void tick(), 3000)
    return () => clearInterval(timer)
  }, [enabled])
}
